import { FC, MouseEventHandler, ReactNode } from 'react'
import { cn } from '@/utils/tailwind/utils'

type Props = {
  children?: ReactNode
  onClick?: MouseEventHandler<HTMLButtonElement>
  href?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  color?: 'yellow' | 'violet' | 'green' | 'red'
  size?: 'small' | 'medium' | 'large'
  outlined?: boolean
  disabled?: boolean
  className?: string
  testId?: string
  ariaLabel?: string
}

const Button: FC<Props> = ({
  children,
  onClick,
  href,
  target,
  color = 'yellow',
  size = 'medium',
  outlined = false,
  disabled = false,
  className = '',
  testId = '',
  ariaLabel,
}) => {
  const borderStyle = 'border focus:ring-2 focus:outline-none '
  const sizeStyle =
    size === 'small' ? 'px-5 py-2 text-xs' : size === 'large' ? 'px-8 py-4 text-lg' : 'text-sm px-5 py-2.5'

  const yellow = 'text-white  border-amber-400 focus:ring-yellow-300 bg-nriched-yellow hover:bg-nriched-yellowDark'
  const violet =
    'bg-nriched-violet text-white font-semibold border-nriched-violet focus:ring-nriched-violetLight hover:bg-nriched-violetDark'
  const green =
    'bg-white text-green-600 hover:text-indigo-950 border-green-600 focus:ring-yellow-300 hover:bg-nriched-yellowLight'
  const red = 'text-white border-transparent  bg-red-500 focus:ring-red-400 hover:bg-red-600'

  const yellowOutline =
    'bg-white text-nriched-yellow hover:text-amber-500 focus:ring-yellow-300 border-2 border-nriched-yellowLight hover:border-nriched-yellow  hover:outline hover:outline-offset-0 hover:outline-1'
  const violetOutline =
    'text-nriched-violetLight hover:text-nriched-violet bg-white hover:bg-slate-50 font-semibold focus:ring-nriched-violetLight border-2 border-nriched-violetLight hover:border-nriched-violet/90 hover:outline hover:outline-offset-0 hover:outline-1'
  const greenOutline =
    'bg-white text-green-600 hover:text-indigo-950 border-green-600 focus:ring-yellow-300 hover:bg-nriched-yellowLight hover:outline hover:outline-offset-0 hover:outline-1'
  const redOutline =
    'text-red-600 hover:text-red-700 border-2 border-red-600 focus:ring-red-400 hover:border-red-700 hover:outline hover:outline-offset-0 hover:outline-1'

  // const disabledStyle = 'text-slate-600 border-slate-400 opacity-50 cursor-not-allowed'
  const disabledStyle = disabled ? 'opacity-50 pointer-events-none' : ''

  const colorStyle = () => {
    switch (color) {
      case 'yellow':
        return outlined ? yellowOutline : yellow
      case 'violet':
        return outlined ? violetOutline : violet
      case 'green':
        return outlined ? greenOutline : green
      case 'red':
        return outlined ? redOutline : red
      default:
        return yellow
    }
  }

  const sharedClassName = cn(
    'inline-block',
    'select-none rounded-lg text-center font-medium subpixel-antialiased disabled:opacity-50',
    borderStyle,
    sizeStyle,
    size === 'small' ? 'text-xs' : size === 'large' ? 'text-lg' : 'text-sm',
    colorStyle(),
    disabledStyle,
    className,
  )

  if (href) {
    return (
      <a
        href={disabled ? undefined : href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        aria-label={ariaLabel}
        aria-disabled={disabled ? true : undefined}
        tabIndex={disabled ? -1 : undefined}
        className={sharedClassName}
        data-testid={testId}>
        {children}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={sharedClassName}
      data-testid={testId}>
      {children}
    </button>
  )
}

export default Button
